/**
 * BuilderRunsTable — the row-per-run table for the report builder's run-list
 * view (and, when reached from a drilled aggregate cell, the drill-down).
 *
 * Driven by a resolved list of run-field columns (`RunFieldDef[]`) plus the
 * report's configured measures (rendered as per-run sample columns). Each row
 * links to RunDetail (level 3). Extracted from the old BuilderRunsPage so the
 * unified BuilderPage owns run rendering.
 */

import React from 'react';
import { Badge } from '@/components/ui';
import { ResponsiveTable } from '@/components/ui/responsive-table';
import type { BuilderConfig, BuilderRunRow, MeasureDef, RunFieldDef } from '@simcheck/shared-types';
import { formatMeasureValue } from '@/pages/reports/reportFormat';
import { useDateFormat, type BoundDateFormatters } from '@/hooks/useDateFormat';

/**
 * Default run-list columns when a config sets no `runColumns` — mirrors
 * DEFAULT_RUN_COLUMNS in apps/frontend-new/functions/src/reports/catalog.ts.
 */
const DEFAULT_RUN_COLUMNS = [
  'run_started', 'run_flow', 'run_status', 'run_duration',
  'run_operator', 'run_location', 'run_esim_profile', 'run_modem', 'run_rat', 'run_failure_class',
];
const CDR_RUN_COLUMNS = ['call_a_number', 'call_b_number', 'call_outcome', 'call_talk_ms'];

/**
 * Measures already covered by a fixed run-field column (so we don't render a
 * redundant per-run measure column): run count (always 1) + duration (the
 * `run_duration` column).
 */
export const MEASURES_WITH_FIXED_COLUMN = new Set(['execution_count', 'duration_ms']);

function statusVariant(status: string, passed: boolean): 'success' | 'error' | 'secondary' {
  if (status === 'completed' && passed) return 'success';
  if (status === 'failed' || !passed) return 'error';
  return 'secondary';
}

/**
 * Resolve which run-field columns to render — mirrors the server's
 * `resolveRunColumns`. Explicit `config.runColumns` wins; otherwise the default
 * set, with the CDR columns auto-inserted after `run_duration` when any run
 * carries call data.
 */
export function resolveRunColumnsClient(
  config: BuilderConfig,
  runs: BuilderRunRow[],
  runFieldById: Map<string, RunFieldDef>,
): RunFieldDef[] {
  let ids: string[];
  if (config.runColumns && config.runColumns.length > 0) {
    ids = config.runColumns;
  } else {
    ids = [...DEFAULT_RUN_COLUMNS];
    const hasCall = runs.some((r) => r.callBNumber || r.callOutcome);
    if (hasCall) {
      const idx = ids.indexOf('run_duration');
      const at = idx >= 0 ? idx + 1 : ids.length;
      ids = [...ids.slice(0, at), ...CDR_RUN_COLUMNS, ...ids.slice(at)];
    }
  }
  return ids.map((id) => runFieldById.get(id)).filter((f): f is RunFieldDef => !!f);
}

function renderRunCell(run: BuilderRunRow, field: RunFieldDef, fmt: BoundDateFormatters): React.ReactNode {
  switch (field.runAccessor) {
    case 'startedAt':
      return <span className="tabular-nums text-sm">{fmt.formatDateTime(run.startedAt)}</span>;
    case 'flowName':
      return <span className="font-medium">{run.flowName ?? run.executionId.slice(0, 16)}</span>;
    case 'status':
      return <Badge variant={statusVariant(run.status, run.passed)}>{run.status}</Badge>;
    case 'durationMs':
      return <span className="tabular-nums text-sm">{run.durationMs !== null ? `${(run.durationMs / 1000).toFixed(1)}s` : '—'}</span>;
    case 'callANumber':
      return <span className="font-mono text-sm">{run.callANumber ?? '—'}</span>;
    case 'callBNumber':
      return <span className="font-mono text-sm">{run.callBNumber ?? '—'}</span>;
    case 'callOutcome':
      return run.callOutcome
        ? <Badge variant={run.callOutcome === 'Answered' ? 'success' : run.callOutcome === 'Dropped' ? 'destructive' : 'secondary'}>{run.callOutcome}</Badge>
        : <span className="text-sm">—</span>;
    case 'callSetupMs':
      return <span className="tabular-nums text-sm">{run.callSetupMs != null ? `${(run.callSetupMs / 1000).toFixed(1)}s` : '—'}</span>;
    case 'callConnectionTimeMs':
      return <span className="tabular-nums text-sm">{run.callConnectionTimeMs != null ? `${(run.callConnectionTimeMs / 1000).toFixed(1)}s` : '—'}</span>;
    case 'callDialedAt':
      return <span className="tabular-nums text-sm">{run.callDialedAt ? fmt.formatTime(run.callDialedAt) : '—'}</span>;
    case 'callConnectedAt':
      return <span className="tabular-nums text-sm">{run.callConnectedAt ? fmt.formatTime(run.callConnectedAt) : '—'}</span>;
    case 'callTalkMs':
      return <span className="tabular-nums text-sm">{run.callTalkMs != null ? `${(run.callTalkMs / 1000).toFixed(1)}s` : '—'}</span>;
    case 'callDtmfHeard':
      return <span className="font-mono text-sm">{run.callDtmfHeard || '—'}</span>;
    case 'callDtmfFirstAt':
      return <span className="tabular-nums text-sm">{run.callDtmfFirstAt ? fmt.formatTime(run.callDtmfFirstAt) : '—'}</span>;
    case 'operatorName':
      return <span className="text-sm">{run.operatorName ?? '—'}{run.mccmnc ? ` (${run.mccmnc})` : ''}</span>;
    case 'locationName':
      return <span className="text-sm">{run.locationName ?? run.locationId ?? '—'}</span>;
    case 'rat':
      return <span className="text-sm">{run.rat ? run.rat.toUpperCase() : '—'}</span>;
    default: {
      const v = run[field.runAccessor];
      const text =
        v === null || v === undefined || typeof v === 'object'
          ? '—'
          : typeof v === 'boolean'
            ? (v ? 'Yes' : 'No')
            : String(v);
      return <span className="text-sm">{text}</span>;
    }
  }
}

function sortValueFor(run: BuilderRunRow, field: RunFieldDef): string | number | null {
  if (field.runAccessor === 'startedAt') return new Date(run.startedAt).getTime();
  const v = run[field.runAccessor];
  if (v === null || v === undefined || typeof v === 'object') return null;
  return typeof v === 'boolean' ? String(v) : v;
}

/** Prettify a flow-parameter key for a column header: `phoneNumber` → "Phone number". */
export function paramColumnLabel(key: string): string {
  const spaced = key.replace(/[_-]+/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export default function BuilderRunsTable({
  runs, columns, paramColumns = [], measureDefs, onRowClick,
}: {
  runs: BuilderRunRow[];
  columns: RunFieldDef[];
  /** Flow-parameter column keys (raw `outputs` keys) rendered after run columns. */
  paramColumns?: string[];
  measureDefs: MeasureDef[];
  onRowClick: (run: BuilderRunRow) => void;
}) {
  const fmt = useDateFormat();
  const tableColumns = [
    ...columns.map((f) => ({
      key: `f_${f.id}`,
      header: f.displayName,
      sortable: true,
      sortValue: (r: BuilderRunRow) => sortValueFor(r, f),
      render: (_: unknown, r: BuilderRunRow) => renderRunCell(r, f, fmt),
    })),
    ...paramColumns.map((key) => ({
      key: `p_${key}`,
      header: paramColumnLabel(key),
      sortable: true,
      sortValue: (r: BuilderRunRow) => {
        const v = r.params?.[key];
        return v === null || v === undefined ? null : (typeof v === 'boolean' ? String(v) : v);
      },
      render: (_: unknown, r: BuilderRunRow) => {
        const v = r.params?.[key];
        return <span className="font-mono text-sm">{v === null || v === undefined ? '—' : String(v)}</span>;
      },
    })),
    ...measureDefs.map((def) => ({
      key: `m_${def.id}`,
      header: def.unit && def.unit !== 'count' && def.unit !== '' ? `${def.displayName} (${def.unit})` : def.displayName,
      sortable: true,
      sortValue: (r: BuilderRunRow) => r.measures?.[def.id] ?? null,
      render: (_: unknown, r: BuilderRunRow) => (
        <span className="tabular-nums text-sm">
          {formatMeasureValue(r.measures?.[def.id] ?? null, def, def.defaultAggregation)}
        </span>
      ),
    })),
  ];

  return (
    <ResponsiveTable<BuilderRunRow>
      data={runs}
      keyExtractor={(r) => r.executionId}
      onRowClick={onRowClick}
      searchable
      searchPlaceholder="Filter runs (flow, operator, location, eSIM, modem, status, RAT, failure)…"
      searchKeys={['flowName', 'operatorName', 'locationName', 'esimProfileName', 'modemModel', 'failureClass', 'mccmnc', 'callANumber', 'callBNumber']}
      paginated
      defaultPageSize={25}
      columns={tableColumns}
    />
  );
}
